/**
 * useAutoSave — debounced background saver for forms.
 *
 * Usage:
 *   const { status, lastSavedAt, saveNow } = useAutoSave(formState, saveFn, {
 *     delay: 800,
 *     enabled: editingDetails,
 *   });
 *
 * `saveFn(payload)` MUST be a stable async function that performs the actual
 * PATCH/POST. The hook compares values via JSON-stringify, so don't pass
 * non-serialisable things.
 *
 * Returns:
 *   status      : 'idle' | 'saving' | 'saved' | 'error'
 *   lastSavedAt : ISO string of last successful save
 *   error       : last error message (when status === 'error')
 *   saveNow()   : manual flush (e.g. on blur / window unload)
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const stableStringify = (v) => {
  try { return JSON.stringify(v); } catch { return String(v); }
};

export const useAutoSave = (value, saveFn, options = {}) => {
  const { delay = 800, enabled = true } = options;
  const [status, setStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [error, setError] = useState(null);

  // Snapshot of the last successfully-saved value (string form).
  const savedRef = useRef(stableStringify(value));
  // Latest scheduled value (for flush on unmount / saveNow).
  const pendingRef = useRef(null);
  const timerRef = useRef(null);
  const saveFnRef = useRef(saveFn);
  saveFnRef.current = saveFn;

  const flush = useCallback(async () => {
    if (!pendingRef.current) return;
    const { snapshotString, payload } = pendingRef.current;
    pendingRef.current = null;
    setStatus('saving');
    try {
      await saveFnRef.current(payload);
      savedRef.current = snapshotString;
      setLastSavedAt(new Date().toISOString());
      setStatus('saved');
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed');
      setStatus('error');
    }
  }, []);

  // Debounced trigger whenever `value` changes.
  useEffect(() => {
    // When editing is switched off, never drop a change still sitting in the
    // debounce window — flush it immediately.
    if (!enabled) {
      flush();
      return undefined;
    }
    const snapshotString = stableStringify(value);
    if (snapshotString === savedRef.current) return undefined;
    pendingRef.current = { snapshotString, payload: value };
    setStatus('unsaved');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { flush(); }, delay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, enabled, delay, flush]);

  // Flush on unmount so a half-typed change is preserved.
  useEffect(() => () => { flush(); }, [flush]);

  // Flush on tab close / visibility hidden so power outages don't lose data.
  useEffect(() => {
    if (!enabled) return undefined;
    const onLeave = () => { flush(); };
    window.addEventListener('beforeunload', onLeave);
    document.addEventListener('visibilitychange', onLeave);
    return () => {
      window.removeEventListener('beforeunload', onLeave);
      document.removeEventListener('visibilitychange', onLeave);
    };
  }, [enabled, flush]);

  return { status, lastSavedAt, error, saveNow: flush };
};

export default useAutoSave;
